import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getExams,
  createExam,
  updateExam,
  deleteExam,
  publishExam,
  archiveExam,
  requestExamValidation,
  validateExam,
  rejectExam,
  reopenExam,
  neutralizeExamQuestion,
  getExamQuestions,
  addExamQuestion,
  removeExamQuestion,
} from "../../../services/ExamService";
import { getSubjects } from "../../../services/SubjectService";
import { getAcademicYears } from "../../../services/AcademicYearService";
import { getSemesters } from "../../../services/SemesterService";
import { getQuestions } from "../../../services/QuestionService";
import DataTablePagination from "../common/DataTablePagination";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";
import { Trans, useTranslation } from "react-i18next";

const statusBadge = { draft: "secondary", validated: "info", published: "success", archived: "warning" };

const emptyForm = {
  subject_id: "",
  academic_year_id: "",
  semester_id: "",
  title: "",
  description: "",
  duration_minutes: 60,
  pass_score: 50,
  shuffle_questions: false,
  shuffle_answers: false,
  show_results_immediately: false,
  require_fullscreen: false,
  block_copy_paste: false,
  is_active: true,
};

// Un brouillon refusé par la commission garde le statut "draft" côté backend,
// mais on l'affiche comme "Rejeté" (le motif est affiché sous le badge).
const getExamStatusInfo = (exam) => {
  if (exam.status === "draft" && exam.rejected_by) return { key: "rejected", variant: "danger" };
  return { key: exam.status, variant: statusBadge[exam.status] || "secondary" };
};

const Exams = () => {
  const { t } = useTranslation();
  // Chaque action est conditionnée par sa permission (les mêmes que celles
  // contrôlées par le backend) : créer/modifier/supprimer, valider (commission),
  // publier, archiver, neutraliser une question.
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.EXAM_CREATE);
  const canUpdate = can(PERMISSIONS.EXAM_UPDATE);
  const canDelete = can(PERMISSIONS.EXAM_DELETE);
  const canValidate = can(PERMISSIONS.EXAM_VALIDATE);
  const canPublish = can(PERMISSIONS.EXAM_PUBLISH);
  const canArchive = can(PERMISSIONS.EXAM_ARCHIVE);
  const canNeutralize = can(PERMISSIONS.EXAM_NEUTRALIZE_QUESTION);

  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [subjects, setSubjects] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [semesters, setSemesters] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  // Modal de composition (questions de l'examen)
  const [compositionExam, setCompositionExam] = useState(null);
  const [examQuestions, setExamQuestions] = useState([]);
  const [loadingComposition, setLoadingComposition] = useState(false);
  const [questionBank, setQuestionBank] = useState([]);
  const [questionToAdd, setQuestionToAdd] = useState("");

  useEffect(() => {
    getSubjects({ page: 1, limit: 200 }).then((res) => setSubjects(res.data?.data || [])).catch(() => { });
    getAcademicYears({ page: 1, limit: 200 }).then((res) => setAcademicYears(res.data?.data || [])).catch(() => { });
    getSemesters({ page: 1, limit: 200 }).then((res) => setSemesters(res.data?.data || [])).catch(() => { });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchExams = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "created_at", sort_dir: "desc" };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const res = await getExams(params);
      const body = res.data;
      setExams(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.exams.impossibleChargerListeExamens"));
      setExams([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter, t]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const label = (list, id) => list.find((x) => x.id === id)?.name || list.find((x) => x.id === id)?.title || "-";

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (exam) => {
    setFormMode("edit");
    setFormData({
      subject_id: exam.subject_id || "",
      academic_year_id: exam.academic_year_id || "",
      semester_id: exam.semester_id || "",
      title: exam.title || "",
      description: exam.description || "",
      duration_minutes: exam.duration_minutes || 60,
      pass_score: exam.pass_score ?? 50,
      shuffle_questions: !!exam.shuffle_questions,
      shuffle_answers: !!exam.shuffle_answers,
      show_results_immediately: false,
      require_fullscreen: !!exam.require_fullscreen,
      block_copy_paste: !!exam.block_copy_paste,
      is_active: !!exam.is_active,
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(exam.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.subject_id) errors.subject_id = t("common.ui.laMatiereEstRequise");
    if (!formData.academic_year_id) errors.academic_year_id = t("common.ui.lAnneeUniversitaireEstRequise");
    if (!formData.title.trim()) errors.title = t("common.ui.leTitreEstRequis");
    if (!formData.duration_minutes || formData.duration_minutes < 1) errors.duration_minutes = t("pages.exams.durationInvalid");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        subject_id: formData.subject_id,
        academic_year_id: formData.academic_year_id,
        semester_id: formData.semester_id || null,
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        duration_minutes: Number(formData.duration_minutes),
        pass_score: formData.pass_score !== "" ? Number(formData.pass_score) : 50,
        shuffle_questions: !!formData.shuffle_questions,
        shuffle_answers: !!formData.shuffle_answers,
        // Résultats toujours masqués après la session (règle de la réunion).
        show_results_immediately: false,
        require_fullscreen: !!formData.require_fullscreen,
        block_copy_paste: !!formData.block_copy_paste,
        is_active: formData.is_active,
      };
      if (formMode === "add") {
        await createExam(payload);
        setFeedback({ type: "success", message: t("pages.exams.createSuccess") });
      } else {
        await updateExam(editingId, payload);
        setFeedback({ type: "success", message: t("pages.exams.updateSuccess") });
      }
      setShowFormModal(false);
      fetchExams();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteExam(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.exams.deleteSuccess") });
      setDeleteTarget(null);
      if (exams.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchExams();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.exams.deleteError") });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handlePublish = async (exam) => {
    try {
      await publishExam(exam.id);
      setFeedback({ type: "success", message: t("pages.exams.publishSuccess") });
      fetchExams();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.exams.publishError") });
    }
  };

  const handleRequestValidation = async (exam) => {
    try {
      await requestExamValidation(exam.id);
      setFeedback({ type: "success", message: t("pages.exams.laCommissionExamensEtePrevenue") });
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.exams.impossibleEnvoyerDemandeValidation") });
    }
  };

  const handleValidate = async (exam) => {
    try {
      await validateExam(exam.id);
      setFeedback({ type: "success", message: t("pages.exams.examenValide") });
      fetchExams();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.exams.impossibleValiderExamen") });
    }
  };

  const handleReopen = async (exam) => {
    try {
      await reopenExam(exam.id);
      setFeedback({ type: "success", message: t("pages.exams.examenRepasseBrouillon") });
      fetchExams();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.exams.impossibleRepasserExamenBrouillon") });
    }
  };

  const handleNeutralize = async (examQuestion) => {
    if (!compositionExam) return;
    const reason = window.prompt("Motif de la neutralisation (facultatif) :", "");
    if (reason === null) return; // annulé
    try {
      await neutralizeExamQuestion(compositionExam.id, examQuestion.id, reason);
      const res = await getExamQuestions(compositionExam.id);
      setExamQuestions(res.data?.data || []);
      setFeedback({ type: "success", message: t("pages.exams.questionNeutraliseeNotesOntEteRecalculees") });
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.exams.impossibleNeutraliserQuestion") });
    }
  };

  const handleArchive = async (exam) => {
    try {
      await archiveExam(exam.id);
      setFeedback({ type: "success", message: t("pages.exams.archiveSuccess") });
      fetchExams();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.exams.archiveError") });
    }
  };

  const openRejectModal = (exam) => {
    setRejectTarget(exam);
    setRejectReason("");
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await rejectExam(rejectTarget.id, rejectReason.trim() || undefined);
      setFeedback({ type: "success", message: t('pages.exams.rejectSuccess') });
      setRejectTarget(null);
      setRejectReason("");
      fetchExams();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t('pages.exams.rejectError') });
    } finally {
      setRejecting(false);
    }
  };

  // --- Composition de l'examen (questions attachées) ---
  const openComposition = async (exam) => {
    setCompositionExam(exam);
    setQuestionToAdd("");
    setLoadingComposition(true);
    try {
      const [compRes, bankRes] = await Promise.all([
        getExamQuestions(exam.id),
        getQuestions({ page: 1, limit: 200, subject_id: exam.subject_id }),
      ]);
      setExamQuestions(compRes.data?.data || []);
      setQuestionBank(bankRes.data?.data || []);
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.exams.loadCompositionError") });
    } finally {
      setLoadingComposition(false);
    }
  };

  const closeComposition = () => setCompositionExam(null);

  const handleAddExamQuestion = async () => {
    if (!questionToAdd || !compositionExam) return;
    try {
      await addExamQuestion(compositionExam.id, { exam_id: compositionExam.id, question_id: questionToAdd });
      const res = await getExamQuestions(compositionExam.id);
      setExamQuestions(res.data?.data || []);
      setQuestionToAdd("");
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.exams.addQuestionError") });
    }
  };

  const handleRemoveExamQuestion = async (examQuestionId) => {
    if (!compositionExam) return;
    try {
      await removeExamQuestion(compositionExam.id, examQuestionId);
      setExamQuestions((prev) => prev.filter((eq) => eq.id !== examQuestionId));
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.exams.removeQuestionError") });
    }
  };

  const availableQuestions = questionBank.filter((q) => !examQuestions.some((eq) => eq.question_id === q.id));

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.exams.title")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            <div className="mb-2">
              {canCreate && (
                <button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                  <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.exams.addLabel")}
                </button>
              )}
            </div>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.exams.title")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <span className="icon-addon"></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("common.ui.rechercherTitre")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ paddingLeft: "35px", minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.crud.allStatuses")}</option>
                  <option value="draft">{t("common.examStatus.draft")}</option>
                  <option value="validated">{t("pages.exams.valide")}</option>
                  <option value="published">{t("common.examStatus.published")}</option>
                  <option value="archived">{t("common.examStatus.archived")}</option>
                </select>
              </div>
            </div>
          </div>

          <DataTablePagination
            variant="header"
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(v) => {
              setLimit(v);
              setPage(1);
            }}
          />

          <div className="card-body p-0 py-3">
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.ui.titre")}</th>
                    <th>{t("common.fields.subject")}</th>
                    <th>{t("common.fields.year")}</th>
                    <th>{t("common.ui.duree")}</th>
                    <th>{t("pages.exams.thresholdColumn")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : exams.length > 0 ? (
                    exams.map((exam) => (
                      <tr key={exam.id}>
                        <td className="fw-medium">{exam.title}</td>
                        <td>{label(subjects, exam.subject_id)}</td>
                        <td>{label(academicYears, exam.academic_year_id)}</td>
                        <td>{t("common.ui.min", { duration_minutes: exam.duration_minutes })}</td>
                        <td>{exam.pass_score}%</td>
                        <td>
                          <span className={`badge badge-soft-${getExamStatusInfo(exam).variant}`}>
                            {t(`common.examStatus.${getExamStatusInfo(exam).key}`, { defaultValue: exam.status })}
                          </span>
                          {exam.status === "draft" && exam.rejected_by && exam.rejection_reason && (
                            <div className="small text-danger mt-1">{exam.rejection_reason}</div>
                          )}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              {canUpdate && exam.status === "draft" && (
                                <Dropdown.Item className="rounded-1" onClick={() => openEditModal(exam)}>
                                  <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                                </Dropdown.Item>
                              )}
                              <Dropdown.Item className="rounded-1" onClick={() => openComposition(exam)}>
                                <i className="ti ti-list-details me-2"></i>
                                {canUpdate && exam.status === "draft" ? t("pages.exams.manageQuestions") : t("pages.exams.voirQuestions")}
                              </Dropdown.Item>
                              {canUpdate && exam.status === "draft" && (
                                <Dropdown.Item className="rounded-1" onClick={() => handleRequestValidation(exam)}>
                                  <i className="ti ti-bell-ringing me-2"></i>{t("pages.exams.demanderValidation")}
                                </Dropdown.Item>
                              )}
                              {canValidate && exam.status === "draft" && (
                                <Dropdown.Item className="rounded-1" onClick={() => handleValidate(exam)}>
                                  <i className="ti ti-circle-check me-2"></i>{t("common.ui.validerCommission")}
                                </Dropdown.Item>
                              )}
                              {canValidate && exam.status === "draft" && (
                                <Dropdown.Item className="rounded-1" onClick={() => openRejectModal(exam)}>
                                  <i className="ti ti-x me-2"></i>{t("pages.exams.reject")}
                                </Dropdown.Item>
                              )}
                              {canValidate && exam.status === "validated" && (
                                <Dropdown.Item className="rounded-1" onClick={() => handleReopen(exam)}>
                                  <i className="ti ti-arrow-back-up me-2"></i>{t("pages.exams.repasserBrouillon")}
                                </Dropdown.Item>
                              )}
                              {canPublish && exam.status === "validated" && (
                                <Dropdown.Item className="rounded-1" onClick={() => handlePublish(exam)}>
                                  <i className="ti ti-send me-2"></i>{t("pages.exams.publish")}
                                </Dropdown.Item>
                              )}
                              {canArchive && exam.status === "published" && (
                                <Dropdown.Item className="rounded-1" onClick={() => handleArchive(exam)}>
                                  <i className="ti ti-archive me-2"></i>{t("pages.exams.archive")}
                                </Dropdown.Item>
                              )}
                              {canDelete && (
                                <Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(exam)}>
                                  <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.exams.noExamsFound")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <DataTablePagination
              variant="footer"
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={(v) => {
                setLimit(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {/* Modal Ajout / Modification */}
      <Modal show={showFormModal} onHide={closeFormModal} centered size="lg">
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.exams.addLabel") : t("pages.exams.editTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  {t("common.fields.subject")} <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${formErrors.subject_id ? "is-invalid" : ""}`}
                  value={formData.subject_id}
                  onChange={(e) => handleFormChange("subject_id", e.target.value)}
                >
                  <option value="">{t("common.crud.selectPlaceholder")}</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {formErrors.subject_id && <div className="invalid-feedback d-block">{formErrors.subject_id}</div>}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  {t("common.fields.academicYear")} <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${formErrors.academic_year_id ? "is-invalid" : ""}`}
                  value={formData.academic_year_id}
                  onChange={(e) => handleFormChange("academic_year_id", e.target.value)}
                >
                  <option value="">{t("common.crud.selectPlaceholder")}</option>
                  {academicYears.map((y) => (
                    <option key={y.id} value={y.id}>
                      {y.name}
                    </option>
                  ))}
                </select>
                {formErrors.academic_year_id && <div className="invalid-feedback d-block">{formErrors.academic_year_id}</div>}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t("pages.exams.semesterOptional")}</label>
                <select
                  className="form-select"
                  value={formData.semester_id}
                  onChange={(e) => handleFormChange("semester_id", e.target.value)}
                >
                  <option value="">{t("common.fields.noneMasc")}</option>
                  {semesters.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">
                {t("common.ui.titre")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${formErrors.title ? "is-invalid" : ""}`}
                value={formData.title}
                onChange={(e) => handleFormChange("title", e.target.value)}
              />
              {formErrors.title && <div className="invalid-feedback">{formErrors.title}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label">{t("common.fields.description")}</label>
              <textarea
                className="form-control"
                rows={2}
                value={formData.description}
                onChange={(e) => handleFormChange("description", e.target.value)}
              ></textarea>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("pages.exams.durationMinutesLabel")} <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className={`form-control ${formErrors.duration_minutes ? "is-invalid" : ""}`}
                  value={formData.duration_minutes}
                  onChange={(e) => handleFormChange("duration_minutes", e.target.value)}
                />
                {formErrors.duration_minutes && <div className="invalid-feedback">{formErrors.duration_minutes}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.exams.passScoreLabel")}</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="form-control"
                  value={formData.pass_score}
                  onChange={(e) => handleFormChange("pass_score", e.target.value)}
                />
              </div>
            </div>
            <div className="d-flex flex-wrap gap-4">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="shuffle-questions"
                  checked={formData.shuffle_questions}
                  onChange={(e) => handleFormChange("shuffle_questions", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="shuffle-questions">
                  {t("pages.exams.shuffleQuestions")}
                </label>
              </div>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="shuffle-answers"
                  checked={formData.shuffle_answers}
                  onChange={(e) => handleFormChange("shuffle_answers", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="shuffle-answers">
                  {t("pages.exams.shuffleAnswers")}
                </label>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-4 mt-2">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="require-fullscreen"
                  checked={formData.require_fullscreen}
                  onChange={(e) => handleFormChange("require_fullscreen", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="require-fullscreen">
                  {t("pages.exams.pleinEcranObligatoirePendantExamen")}
                </label>
              </div>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="block-copy-paste"
                  checked={formData.block_copy_paste}
                  onChange={(e) => handleFormChange("block_copy_paste", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="block-copy-paste">
                  {t("pages.exams.bloquerCopierColler")}
                </label>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Modal Suppression */}
      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>
            <Trans i18nKey="pages.exams.voulezVousVraimentSupprimerExamen" values={{ title: deleteTarget?.title }} components={[<strong />]} />
          </p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Rejet */}
      <Modal show={rejectTarget !== null} onHide={() => (rejecting ? null : setRejectTarget(null))} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('pages.exams.rejectTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('pages.exams.confirmRejectBody', { title: rejectTarget?.title || "" })}</p>
          <label className="form-label">{t('pages.exams.rejectReasonLabel')}</label>
          <textarea
            className="form-control"
            rows={3}
            maxLength={500}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          ></textarea>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={() => setRejectTarget(null)} disabled={rejecting}>
            {t('common.crud.cancel')}
          </button>
          <button type="button" className="btn btn-danger" onClick={confirmReject} disabled={rejecting}>
            {rejecting ? t('common.crud.saving') : t('pages.exams.confirmReject')}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Modal Composition de l'examen */}
      <Modal show={compositionExam !== null} onHide={closeComposition} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("pages.exams.compositionTitle", { title: compositionExam?.title })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {compositionExam && compositionExam.status !== "draft" && (
            <div className="alert alert-warning">
              {t("pages.exams.compositionDraftOnlyNotice")}
              {compositionExam.status === "validated" && t("pages.exams.repassezBrouillonPourModifier")}
            </div>
          )}
          {loadingComposition ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("common.crud.loading")}</span>
              </div>
            </div>
          ) : (
            <>
              {canUpdate && compositionExam?.status === "draft" && (
                <div className="d-flex gap-2 mb-3">
                  <select className="form-select" value={questionToAdd} onChange={(e) => setQuestionToAdd(e.target.value)}>
                    <option value="">{t("pages.exams.selectQuestionToAdd")}</option>
                    {availableQuestions.map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.content.slice(0, 80)}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-primary text-nowrap" onClick={handleAddExamQuestion} disabled={!questionToAdd}>
                    {t("common.crud.add")}
                  </button>
                </div>
              )}
              <div className="table-responsive" style={{ maxWidth: "100%" }}>
                <table
                  className="table table-sm align-middle"
                  style={{
                    width: "100%",
                    tableLayout: "fixed",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: "50px" }}>#</th>

                      <th style={{ width: "auto" }}>
                        {t("common.ui.question")}
                      </th>

                      <th style={{ width: "90px", whiteSpace: "nowrap" }}>
                        {t("common.fields.points")}
                      </th>

                      {((canUpdate && compositionExam?.status === "draft") ||
                        (canNeutralize &&
                          ["published", "archived"].includes(compositionExam?.status))) && (
                          <th style={{ width: "100px" }}></th>
                        )}
                    </tr>
                  </thead>

                  <tbody>
                    {examQuestions.length > 0 ? (
                      examQuestions.map((eq, idx) => (
                        <tr key={eq.id}>
                          <td>{idx + 1}</td>

                          <td
                            style={{
                              whiteSpace: "normal",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            <div
                              style={{
                                maxWidth: "100%",
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {eq.content}

                              {eq.is_neutralized && (
                                <span
                                  className="badge badge-soft-warning ms-2"
                                  title={eq.neutralization_reason || ""}
                                >
                                  {t("pages.exams.neutralisee")}
                                </span>
                              )}
                            </div>
                          </td>

                          <td style={{ whiteSpace: "nowrap" }}>
                            {eq.points}
                          </td>

                          {canUpdate && compositionExam?.status === "draft" && (
                            <td style={{ whiteSpace: "nowrap" }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveExamQuestion(eq.id)}
                                title={t("common.crud.delete")}
                              >
                                <i className="ti ti-x"></i>
                              </button>
                            </td>
                          )}

                          {canNeutralize &&
                            ["published", "archived"].includes(compositionExam?.status) && (
                              <td style={{ whiteSpace: "nowrap" }}>
                                {!eq.is_neutralized && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning text-nowrap"
                                    onClick={() => handleNeutralize(eq)}
                                  >
                                    {t("pages.exams.neutraliser")}
                                  </button>
                                )}
                              </td>
                            )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={4}
                          className="text-center text-muted py-3"
                        >
                          {t("pages.exams.noQuestionsAdded")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={closeComposition}>
            {t("common.crud.close")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Exams;